import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import {
    createUser,
    deleteUser,
    getUserPermissions,
    listDevices,
    listUsers,
    updateDevice,
    updateUser,
    updateUserPermissions,
} from '../services/admin';
import './AdminPanel.css';

const DEVICE_TYPE_ORDER = ['RA', 'ALC', 'SIS', 'OW', 'TI', 'OTHER'];
const DEVICE_TYPE_LABELS = {
    RA: 'RA 雨量監測計',
    ALC: 'ALC 地錨荷重計',
    SIS: 'SIS 坍方偵測器',
    OW: 'OW 水位觀測井',
    TI: 'TI 傾斜計',
    OTHER: '其他',
};

const normalizeDeviceType = (value = '') => {
    const text = `${value}`.trim().toUpperCase();
    if (!text) return 'OTHER';
    if (text.includes('RAIN') || text === 'RA') return 'RA';
    if (text.includes('FORCE') || text.includes('ALC') || text.includes('LOAD'))
        return 'ALC';
    if (text.includes('LANDSLIDE') || text.includes('LS') || text === 'SIS') return 'SIS';
    if (text.includes('WATER') || text === 'OW') return 'OW';
    if (text.includes('TILT') || text === 'TI') return 'TI';
    return text;
};

const getDeviceDistrict = (device) =>
    device?.district ||
    device?.slope_district ||
    device?.slopeDistrict ||
    device?.segment ||
    device?.area ||
    device?.region ||
    device?.slope?.district ||
    device?.slope?.segment ||
    '未分區';

const getDeviceDistrictRaw = (device) =>
    device?.district ||
    device?.slope_district ||
    device?.slopeDistrict ||
    device?.segment ||
    device?.area ||
    device?.region ||
    device?.slope?.district ||
    device?.slope?.segment ||
    '';

const getDeviceName = (device) =>
    device?.name || device?.device_name || device?.deviceName || '-';

const getDeviceType = (device) =>
    normalizeDeviceType(
        device?.type || device?.device_type || device?.deviceType || device?.kind || ''
    );

const getDeviceTypeRaw = (device) =>
    device?.type || device?.device_type || device?.deviceType || device?.kind || '';

const getDeviceId = (device) =>
    device?.id || device?.device_id || device?.deviceId || device?.deviceID || null;

const isExcludedFromBulkPermissionSelect = (device) => {
    const name = (getDeviceName(device) || '').toString().toLowerCase();
    return name.includes('test') || name.includes('舊');
};

const getDeviceMonitorStatus = (device) => {
    const xmlEnabled = !!device?.enable_xml;
    const thresholdEnabled = !!device?.enable_threshold_detect;
    return {
        xmlEnabled,
        thresholdEnabled,
    };
};

const getUserId = (user) =>
    user?.id || user?.user_id || user?.userId || user?.account_id || null;

const pickRoleText = (user) =>
    user?.role || user?.role_name || user?.roleName || user?.permission || '-';

const pickUserName = (user) =>
    user?.full_name || user?.name || user?.display_name || user?.username || '-';

const pickUserEmail = (user) => user?.email || user?.account || '-';
const pickUserSlope = (user) =>
    user?.slope?.name || user?.slope_name || user?.slope || '-';
const pickUserInstitution = (user) =>
    user?.institution || user?.institution_name || user?.unit || '-';

const getUserTelegramId = (user) =>
    user?.telegram_chat_id || user?.telegramChatId || user?.telegram_chat || null;

const ROLE_OPTIONS = [
    '',
    '陸島人員',
    '工務段承辦',
    '工務段段長',
    '工務科承辦',
    '工務科科長',
    '分局副局長',
];

const parseOptionalNumber = (value) => {
    if (value === '' || value == null) return undefined;
    const num = Number(value);
    return Number.isFinite(num) ? num : undefined;
};

const buildDevicePayload = (form) => ({
    name: form.name || undefined,
    type: form.type || undefined,
    district: form.district === '' ? '' : form.district || undefined,
    transfer_type: form.transfer_type || undefined,
    model: form.model || undefined,
    slope_name: form.slope_name || undefined,
    interval: parseOptionalNumber(form.interval),
    lat: parseOptionalNumber(form.lat),
    lng: parseOptionalNumber(form.lng),
    enable_xml: !!form.enable_xml,
    enable_threshold_detect: !!form.enable_threshold_detect,
});

const buildUserPayload = (form) => {
    const slopeValue = form.slope === '' ? '' : form.slope || undefined;
    const institutionValue =
        form.institution === '' ? '' : form.institution || undefined;
    return {
        full_name: form.full_name || undefined,
        email: form.email || undefined,
        role: form.role || undefined,
        password: form.password || undefined,
        remark: form.remark || undefined,
        slope: slopeValue,
        slope_name: slopeValue,
        institution: institutionValue,
        institution_name: institutionValue,
        unit: institutionValue,
        telegram_chat_id: form.telegram_chat_id || undefined,
    };
};

const DistrictToggle = ({ label, checked, indeterminate, onChange, meta }) => {
    const inputRef = useRef(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.indeterminate = indeterminate;
        }
    }, [indeterminate]);

    return (
        <label className="admin-panel__district-toggle">
            <input
                ref={inputRef}
                type="checkbox"
                checked={checked}
                onChange={onChange}
            />
            <span className="admin-panel__district-label">{label}</span>
            <span className="admin-panel__district-meta">{meta}</span>
        </label>
    );
};

export default function AdminPanel() {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('devices');
    const [activeDeviceType, setActiveDeviceType] = useState('');
    const [devices, setDevices] = useState([]);
    const [users, setUsers] = useState([]);
    const [loadingDevices, setLoadingDevices] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [deviceModal, setDeviceModal] = useState(null);
    const [deviceForm, setDeviceForm] = useState(null);
    const [savingDevice, setSavingDevice] = useState(false);
    const [userModal, setUserModal] = useState(null);
    const [userForm, setUserForm] = useState(null);
    const [savingUser, setSavingUser] = useState(false);
    const [userActionLoading, setUserActionLoading] = useState(null);
    const [permissionUser, setPermissionUser] = useState(null);
    const [permissionIds, setPermissionIds] = useState(new Set());
    const [loadingPermissions, setLoadingPermissions] = useState(false);
    const [savingPermissions, setSavingPermissions] = useState(false);
    const [permissionError, setPermissionError] = useState('');

    const isAdmin = useMemo(() => {
        const role = user?.role;
        if (!role) return false;
        const roleStr = String(role).trim();
        return roleStr==='admin' || roleStr === '陸島人員';
    }, [user?.role]);

    const fetchDevices = useCallback(async () => {
        const controller = new AbortController();
        setLoadingDevices(true);
        setErrorMessage('');
        try {
            const data = await listDevices(controller.signal);
            setDevices(Array.isArray(data) ? data : []);
        } catch (error) {
            setErrorMessage(error?.message || '無法取得儀器資料');
        } finally {
            setLoadingDevices(false);
        }
        return () => controller.abort();
    }, []);

    const fetchUsers = useCallback(async () => {
        const controller = new AbortController();
        setLoadingUsers(true);
        setErrorMessage('');
        try {
            const data = await listUsers(controller.signal);
            setUsers(Array.isArray(data) ? data : []);
        } catch (error) {
            setErrorMessage(error?.message || '無法取得用戶資料');
        } finally {
            setLoadingUsers(false);
        }
        return () => controller.abort();
    }, []);

    useEffect(() => {
        fetchDevices();
        fetchUsers();
    }, [fetchDevices, fetchUsers]);

    useEffect(() => {
        const isModalOpen = !!deviceModal || !!permissionUser || !!userModal;
        document.body.style.overflow = isModalOpen ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [deviceModal, permissionUser, userModal]);

    const devicesByType = useMemo(() => {
        const groups = new Map();
        devices.forEach((device) => {
            const typeKey = getDeviceType(device);
            if (!groups.has(typeKey)) groups.set(typeKey, []);
            groups.get(typeKey).push(device);
        });
        return groups;
    }, [devices]);

    const devicesByDistrict = useMemo(() => {
        const groups = new Map();
        devices.forEach((device) => {
            const district = getDeviceDistrict(device);
            if (!groups.has(district)) groups.set(district, []);
            groups.get(district).push(device);
        });
        return groups;
    }, [devices]);

    const sortedUsers = useMemo(() => {
        return [...users].sort((a, b) => {
            const aId = getUserId(a);
            const bId = getUserId(b);
            if (aId == null && bId == null) return 0;
            if (aId == null) return 1;
            if (bId == null) return -1;
            const aNum = Number(aId);
            const bNum = Number(bId);
            if (Number.isFinite(aNum) && Number.isFinite(bNum)) {
                return aNum - bNum;
            }
            return String(aId).localeCompare(String(bId), 'en');
        });
    }, [users]);

    const orderedTypeKeys = useMemo(() => {
        const keys = Array.from(devicesByType.keys());
        const known = DEVICE_TYPE_ORDER.filter((key) => keys.includes(key));
        const unknown = keys
            .filter((key) => !DEVICE_TYPE_ORDER.includes(key))
            .sort((a, b) => a.localeCompare(b));
        return [...known, ...unknown];
    }, [devicesByType]);

    useEffect(() => {
        if (!orderedTypeKeys.length) return;
        if (!activeDeviceType || (!orderedTypeKeys.includes(activeDeviceType) && activeDeviceType !== 'ALL')) {
            setActiveDeviceType(orderedTypeKeys[0]);
        }
    }, [activeDeviceType, orderedTypeKeys]);

    const deviceTypeTabs = useMemo(
        () => ['ALL', ...orderedTypeKeys],
        [orderedTypeKeys]
    );

    const visibleTypeKeys = useMemo(() => {
        if (activeDeviceType === 'ALL') return orderedTypeKeys;
        return activeDeviceType ? [activeDeviceType] : orderedTypeKeys;
    }, [activeDeviceType, orderedTypeKeys]);

    const orderedDistricts = useMemo(() => {
        return Array.from(devicesByDistrict.keys()).sort((a, b) =>
            a.localeCompare(b, 'zh-Hant')
        );
    }, [devicesByDistrict]);

    const openDeviceEditor = (device) => {
        setDeviceModal(device);
        setDeviceForm({
            id: getDeviceId(device),
            name: device?.name || '',
            type: getDeviceTypeRaw(device),
            district: getDeviceDistrictRaw(device),
            transfer_type: device?.transfer_type || device?.transferType || '',
            model: device?.model || '',
            slope_name: device?.slope_name || device?.slopeName || '',
            interval: device?.interval ?? '',
            lat: device?.lat ?? '',
            lng: device?.lng ?? '',
            enable_xml: !!device?.enable_xml,
            enable_threshold_detect: !!device?.enable_threshold_detect,
        });
    };

    const closeDeviceEditor = () => {
        setDeviceModal(null);
        setDeviceForm(null);
        setSavingDevice(false);
    };

    const saveDevice = async () => {
        if (!deviceModal || !deviceForm) return;
        setSavingDevice(true);
        try {
            const payload = buildDevicePayload(deviceForm);
            const updated = await updateDevice(deviceForm.id, payload);
            const mergedUpdate =
                updated && typeof updated === 'object' ? updated : {};
            setDevices((prev) =>
                prev.map((item) =>
                    getDeviceId(item) === deviceForm.id
                        ? { ...item, ...payload, ...mergedUpdate }
                        : item
                )
            );
            fetchDevices();
            closeDeviceEditor();
        } catch (error) {
            setErrorMessage(error?.message || '更新儀器失敗');
            setSavingDevice(false);
        }
    };

    const openUserEditor = (selectedUser) => {
        const userId = getUserId(selectedUser);
        if (!userId) return;
        setUserModal({ mode: 'edit', user: selectedUser });
        setUserForm({
            id: userId,
            full_name: selectedUser?.full_name || selectedUser?.name || '',
            email: selectedUser?.email || selectedUser?.account || '',
            role: selectedUser?.role || selectedUser?.role_name || '',
            password: '',
            remark: selectedUser?.remark || '',
            slope:
                selectedUser?.slope?.name ||
                selectedUser?.slope_name ||
                selectedUser?.slope ||
                '',
            institution:
                selectedUser?.institution ||
                selectedUser?.institution_name ||
                selectedUser?.unit ||
                '',
            telegram_chat_id: getUserTelegramId(selectedUser) || '',
        });
    };

    const openUserCreator = () => {
        setUserModal({ mode: 'create', user: null });
        setUserForm({
            full_name: '',
            email: '',
            role: '',
            password: '',
            remark: '',
            slope: '',
            institution: '',
            telegram_chat_id: '',
        });
    };

    const closeUserEditor = () => {
        setUserModal(null);
        setUserForm(null);
        setSavingUser(false);
    };

    const saveUser = async () => {
        if (!userModal || !userForm) return;
        setSavingUser(true);
        setErrorMessage('');
        try {
            const payload = buildUserPayload(userForm);
            if (userModal.mode === 'create') {
                if (
                    !payload.full_name ||
                    !payload.email ||
                    !payload.password ||
                    !payload.role
                ) {
                    setErrorMessage('請填寫姓名、Email、密碼、角色');
                    setSavingUser(false);
                    return;
                }
                const created = await createUser(payload);
                setUsers((prev) => [created, ...prev]);
            } else {
                if (
                    !payload.full_name ||
                    !payload.email ||
                    !payload.role
                ) {
                    setErrorMessage('請填寫姓名、Email、角色');
                    setSavingUser(false);
                    return;
                }
                const updated = await updateUser(userForm.id, payload);
                setUsers((prev) =>
                    prev.map((item) =>
                        getUserId(item) === userForm.id ? { ...item, ...updated } : item
                    )
                );
            }
            await fetchUsers();
            closeUserEditor();
        } catch (error) {
            setErrorMessage(error?.message || '更新用戶失敗');
            setSavingUser(false);
        }
    };

    const removeTelegram = async (selectedUser) => {
        const userId = getUserId(selectedUser);
        if (!userId) return;
        if (!window.confirm('確定要解除此用戶的 Telegram 綁定？')) return;
        setUserActionLoading(userId);
        setErrorMessage('');
        try {
            const updated = await updateUser(userId, { telegram_chat_id: '' });
            setUsers((prev) =>
                prev.map((item) =>
                    getUserId(item) === userId ? { ...item, ...updated } : item
                )
            );
            if (userForm?.id === userId) {
                setUserForm((prev) =>
                    prev ? { ...prev, telegram_chat_id: '' } : prev
                );
            }
        } catch (error) {
            setErrorMessage(error?.message || '解除 Telegram 失敗');
        } finally {
            setUserActionLoading(null);
        }
    };

    const removeUser = async (selectedUser) => {
        const userId = getUserId(selectedUser);
        if (!userId) return;
        const displayName = pickUserName(selectedUser);
        const confirmText = `輸入「${displayName}」以刪除該用戶`;
        const hintText = `提示：輸入${displayName}已刪除該用戶`;
        const input = window.prompt(`${confirmText}\n${hintText}`);
        if (input === null) return;
        if (input !== displayName) {
            window.alert('輸入錯誤');
            setErrorMessage('輸入錯誤，未刪除該用戶');
            return;
        }
        setUserActionLoading(userId);
        setErrorMessage('');
        try {
            await deleteUser(userId);
            setUsers((prev) => prev.filter((item) => getUserId(item) !== userId));
            closeUserEditor();
        } catch (error) {
            setErrorMessage(error?.message || '刪除用戶失敗');
        } finally {
            setUserActionLoading(null);
        }
    };

    const openPermissionEditor = async (selectedUser) => {
        const userId = getUserId(selectedUser);
        if (!userId) return;
        setPermissionUser(selectedUser);
        setPermissionIds(new Set());
        setPermissionError('');
        setLoadingPermissions(true);
        try {
            const data = await getUserPermissions(userId);
            const rawIds = data?.device_ids;
            let ids = [];
            if (Array.isArray(rawIds)) {
                ids = rawIds;
            } else if (typeof rawIds === 'string') {
                try {
                    const parsed = JSON.parse(rawIds);
                    ids = Array.isArray(parsed) ? parsed : [];
                } catch (error) {
                    ids = [];
                }
            }
            setPermissionIds(new Set(ids));
        } catch (error) {
            setPermissionError(error?.message || '讀取權限失敗');
        } finally {
            setLoadingPermissions(false);
        }
    };

    const closePermissionEditor = () => {
        setPermissionUser(null);
        setPermissionIds(new Set());
        setPermissionError('');
        setSavingPermissions(false);
    };

    const toggleDevicePermission = (deviceId) => {
        setPermissionIds((prev) => {
            const next = new Set(prev);
            if (next.has(deviceId)) {
                next.delete(deviceId);
            } else {
                next.add(deviceId);
            }
            return next;
        });
    };

    const toggleDistrictPermissions = (district) => {
        const list = devicesByDistrict.get(district) || [];
        const ids = list
            .filter((device) => !isExcludedFromBulkPermissionSelect(device))
            .map((device) => getDeviceId(device))
            .filter(Boolean);
        setPermissionIds((prev) => {
            const next = new Set(prev);
            const allSelected = ids.every((id) => next.has(id));
            ids.forEach((id) => {
                if (allSelected) {
                    next.delete(id);
                } else {
                    next.add(id);
                }
            });
            return next;
        });
    };

    const setAllPermissions = (checked) => {
        if (!checked) {
            setPermissionIds(new Set());
            return;
        }
        const allIds = devices
            .filter((device) => !isExcludedFromBulkPermissionSelect(device))
            .map((device) => getDeviceId(device))
            .filter(Boolean);
        setPermissionIds(new Set(allIds));
    };

    const savePermissions = async () => {
        if (!permissionUser) return;
        const userId = getUserId(permissionUser);
        if (!userId) return;
        setSavingPermissions(true);
        setPermissionError('');
        try {
            await updateUserPermissions(userId, {
                device_ids: Array.from(permissionIds),
            });
            closePermissionEditor();
        } catch (error) {
            setPermissionError(error?.message || '更新權限失敗');
            setSavingPermissions(false);
        }
    };

    if (!isAdmin) {
        return <Navigate to="/" replace />;
    }

    return (
        <div className="admin-panel">
            <header className="admin-panel__hero">
                <div className="admin-panel__hero-content">
                    <p className="admin-panel__eyebrow">管理後台</p>
                    <h1>系統管理中心</h1>
                    <p className="admin-panel__subtitle">
                        僅限系統管理者使用，用於管理儀器與用戶權限。
                    </p>
                </div>
                <div className="admin-panel__hero-card">
                    <div className="admin-panel__hero-label">快速總覽</div>
                    <div className="admin-panel__hero-metrics">
                        <div>
                            <span className="admin-panel__metric">{devices.length}</span>
                            <span className="admin-panel__metric-label">儀器</span>
                        </div>
                        <div>
                            <span className="admin-panel__metric">{users.length}</span>
                            <span className="admin-panel__metric-label">用戶</span>
                        </div>
                    </div>
                </div>
            </header>

            <div className="admin-panel__tabs">
                <button
                    type="button"
                    className={`admin-panel__tab${
                        activeTab === 'devices' ? ' admin-panel__tab--active' : ''
                    }`}
                    onClick={() => setActiveTab('devices')}
                >
                    儀器管理
                </button>
                <button
                    type="button"
                    className={`admin-panel__tab${
                        activeTab === 'users' ? ' admin-panel__tab--active' : ''
                    }`}
                    onClick={() => setActiveTab('users')}
                >
                    用戶管理
                </button>
            </div>

            {errorMessage && (
                <div className="admin-panel__alert">{errorMessage}</div>
            )}

            {activeTab === 'devices' && (
                <section className="admin-panel__section">
                    <div className="admin-panel__section-header">
                        <div>
                            <h2>依儀器類型管理</h2>
                            <p>按類型分組，快速檢視與編輯儀器資訊。</p>
                        </div>
                        <button
                            type="button"
                            className="admin-panel__ghost"
                            onClick={fetchDevices}
                            disabled={loadingDevices}
                        >
                            重新整理
                        </button>
                    </div>
                    <div className="admin-panel__type-tabs">
                        {deviceTypeTabs.map((typeKey) => (
                            <button
                                key={typeKey}
                                type="button"
                                className={`admin-panel__type-tab${
                                    activeDeviceType === typeKey
                                        ? ' admin-panel__type-tab--active'
                                        : ''
                                }`}
                                onClick={() => setActiveDeviceType(typeKey)}
                            >
                                <span>
                                    {typeKey === 'ALL'
                                        ? '全部'
                                        : DEVICE_TYPE_LABELS[typeKey] || typeKey}
                                </span>
                                <span className="admin-panel__type-count">
                                    {typeKey === 'ALL'
                                        ? devices.length
                                        : (devicesByType.get(typeKey) || []).length}
                                </span>
                            </button>
                        ))}
                    </div>

                    {loadingDevices ? (
                        <div className="admin-panel__loading">載入儀器資料中...</div>
                    ) : orderedTypeKeys.length === 0 ? (
                        <div className="admin-panel__loading">尚無儀器資料</div>
                    ) : (
                        <div className="admin-panel__device-groups">
                            {visibleTypeKeys.map((typeKey) => {
                                const list = devicesByType.get(typeKey) || [];
                                return (
                                    <div
                                        key={typeKey}
                                        className="admin-panel__card admin-panel__card--device"
                                    >
                                        <div className="admin-panel__card-header">
                                            <div>
                                                <h3>{DEVICE_TYPE_LABELS[typeKey] || typeKey}</h3>
                                                <span className="admin-panel__chip">
                                                    {list.length} 筆
                                                </span>
                                            </div>
                                        </div>
                                        <div className="admin-panel__table admin-panel__table--device">
                                            <div className="admin-panel__table-row admin-panel__table-row--head">
                                                <span>名稱</span>
                                                <span>區域</span>
                                                <span>型號</span>
                                                <span>傳輸</span>
                                                <span>XML與警戒狀態</span>
                                                <span>操作</span>
                                            </div>
                                            {list.map((device) => {
                                                const deviceId = getDeviceId(device);
                                                const monitorStatus =
                                                    getDeviceMonitorStatus(device);
                                                return (
                                                    <div
                                                        key={deviceId || getDeviceName(device)}
                                                        className="admin-panel__table-row"
                                                    >
                                                        <span>{getDeviceName(device)}</span>
                                                        <span>{getDeviceDistrict(device)}</span>
                                                        <span>{device?.model || '-'}</span>
                                                        <span>
                                                            {device?.transfer_type ||
                                                                device?.transferType ||
                                                                '-'}
                                                        </span>
                                                        <span
                                                            className="admin-panel__device-monitor"
                                                        >
                                                            <span
                                                                className={
                                                                    monitorStatus.xmlEnabled
                                                                        ? 'admin-panel__device-monitor--on'
                                                                        : 'admin-panel__device-monitor--off'
                                                                }
                                                            >
                                                                {monitorStatus.xmlEnabled
                                                                    ? '開啟XML'
                                                                    : '關閉XML'}
                                                            </span>
                                                            <span className="admin-panel__device-monitor-separator">
                                                                /
                                                            </span>
                                                            <span
                                                                className={
                                                                    monitorStatus.thresholdEnabled
                                                                        ? 'admin-panel__device-monitor--on'
                                                                        : 'admin-panel__device-monitor--off'
                                                                }
                                                            >
                                                                {monitorStatus.thresholdEnabled
                                                                    ? '開啟警戒偵測'
                                                                    : '關閉警戒偵測'}
                                                            </span>
                                                        </span>
                                                        <span>
                                                            <button
                                                                type="button"
                                                                className="admin-panel__text-btn"
                                                                onClick={() =>
                                                                    openDeviceEditor(device)
                                                                }
                                                            >
                                                                編輯
                                                            </button>
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>
            )}

            {activeTab === 'users' && (
                <section className="admin-panel__section">
                    <div className="admin-panel__section-header">
                        <div>
                            <h2>用戶與權限</h2>
                            <p>管理帳號資料並調整儀器可視權限。</p>
                        </div>
                        <div className="admin-panel__actions">
                            <button
                                type="button"
                                className="admin-panel__ghost"
                                onClick={openUserCreator}
                            >
                                新增用戶
                            </button>
                            <button
                                type="button"
                                className="admin-panel__ghost"
                                onClick={fetchUsers}
                                disabled={loadingUsers}
                            >
                                重新整理
                            </button>
                        </div>
                    </div>
                    {loadingUsers ? (
                        <div className="admin-panel__loading">載入用戶資料中...</div>
                    ) : (
                        <div className="admin-panel__card admin-panel__card--user">
                            <div className="admin-panel__table admin-panel__table--user">
                                <div className="admin-panel__table-row admin-panel__table-row--head">
                                    <span>姓名</span>
                                    <span>Email</span>
                                    <span>邊坡</span>
                                    <span>單位</span>
                                    <span>角色</span>
                                    <span>Telegram</span>
                                    <span>操作</span>
                                </div>
                                {sortedUsers.map((account) => {
                                    const userId = getUserId(account);
                                    const telegramId = getUserTelegramId(account);
                                    const telegramStatus = telegramId ? '已綁定' : '未綁定';
                                    return (
                                        <div
                                            key={userId || pickUserEmail(account)}
                                            className="admin-panel__table-row"
                                        >
                                            <span>{pickUserName(account)}</span>
                                            <span>{pickUserEmail(account)}</span>
                                            <span>{pickUserSlope(account)}</span>
                                            <span>{pickUserInstitution(account)}</span>
                                            <span>{pickRoleText(account)}</span>
                                            <span>
                                                <span
                                                    className={`admin-panel__status${
                                                        telegramId
                                                            ? ' admin-panel__status--ok'
                                                            : ' admin-panel__status--off'
                                                    }`}
                                                >
                                                    {telegramStatus}
                                                </span>
                                            </span>
                                            <span className="admin-panel__row-actions">
                                                <button
                                                    type="button"
                                                    className="admin-panel__text-btn"
                                                    onClick={() => openUserEditor(account)}
                                                >
                                                    編輯資料
                                                </button>
                                                <button
                                                    type="button"
                                                    className="admin-panel__text-btn"
                                                    onClick={() => openPermissionEditor(account)}
                                                >
                                                    編輯權限
                                                </button>
                                                {telegramId && (
                                                    <button
                                                        type="button"
                                                        className="admin-panel__text-btn admin-panel__text-btn--danger"
                                                        onClick={() => removeTelegram(account)}
                                                        disabled={userActionLoading === userId}
                                                    >
                                                        解除 Telegram
                                                    </button>
                                                )}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </section>
            )}

            {deviceModal && deviceForm && (
                <div className="admin-panel__modal">
                    <div
                        className="admin-panel__modal-backdrop"
                        onClick={closeDeviceEditor}
                    />
                    <div className="admin-panel__modal-card">
                        <div className="admin-panel__modal-header">
                            <h3>編輯儀器</h3>
                            <button type="button" onClick={closeDeviceEditor}>
                                關閉
                            </button>
                        </div>
                        <div className="admin-panel__modal-body">
                            <label>
                                名稱
                                <input
                                    value={deviceForm.name}
                                    disabled
                                    onChange={(event) =>
                                        setDeviceForm((prev) => ({
                                            ...prev,
                                            name: event.target.value,
                                        }))
                                    }
                                />
                            </label>
                            <label>
                                類型
                                <input
                                    value={
                                        DEVICE_TYPE_LABELS[
                                            normalizeDeviceType(deviceForm.type)
                                        ] || deviceForm.type
                                    }
                                    disabled
                                />
                            </label>
                            <label>
                                區域
                                <input
                                    value={deviceForm.district}
                                    onChange={(event) =>
                                        setDeviceForm((prev) => ({
                                            ...prev,
                                            district: event.target.value,
                                        }))
                                    }
                                />
                            </label>
                            <label>
                                傳輸方式
                                <input
                                    value={deviceForm.transfer_type}
                                    onChange={(event) =>
                                        setDeviceForm((prev) => ({
                                            ...prev,
                                            transfer_type: event.target.value,
                                        }))
                                    }
                                />
                            </label>
                            <label>
                                型號
                                <input
                                    value={deviceForm.model}
                                    onChange={(event) =>
                                        setDeviceForm((prev) => ({
                                            ...prev,
                                            model: event.target.value,
                                        }))
                                    }
                                />
                            </label>
                            <label>
                                邊坡名稱
                                <input
                                    value={deviceForm.slope_name}
                                    onChange={(event) =>
                                        setDeviceForm((prev) => ({
                                            ...prev,
                                            slope_name: event.target.value,
                                        }))
                                    }
                                />
                            </label>
                            <label>
                                量測間隔(秒)
                                <input
                                    type="number"
                                    value={deviceForm.interval}
                                    onChange={(event) =>
                                        setDeviceForm((prev) => ({
                                            ...prev,
                                            interval: event.target.value,
                                        }))
                                    }
                                />
                            </label>
                            <label>
                                緯度
                                <input
                                    type="number"
                                    value={deviceForm.lat}
                                    onChange={(event) =>
                                        setDeviceForm((prev) => ({
                                            ...prev,
                                            lat: event.target.value,
                                        }))
                                    }
                                />
                            </label>
                            <label>
                                經度
                                <input
                                    type="number"
                                    value={deviceForm.lng}
                                    onChange={(event) =>
                                        setDeviceForm((prev) => ({
                                            ...prev,
                                            lng: event.target.value,
                                        }))
                                    }
                                />
                            </label>
                            <div className="admin-panel__toggle-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={deviceForm.enable_xml}
                                        onChange={(event) =>
                                            setDeviceForm((prev) => ({
                                                ...prev,
                                                enable_xml: event.target.checked,
                                            }))
                                        }
                                    />
                                    啟用 XML
                                </label>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={deviceForm.enable_threshold_detect}
                                        onChange={(event) =>
                                            setDeviceForm((prev) => ({
                                                ...prev,
                                                enable_threshold_detect: event.target.checked,
                                            }))
                                        }
                                    />
                                    啟用警戒偵測
                                </label>
                            </div>
                        </div>
                        <div className="admin-panel__modal-footer">
                            <button
                                type="button"
                                className="admin-panel__ghost"
                                onClick={closeDeviceEditor}
                                disabled={savingDevice}
                            >
                                取消
                            </button>
                            <button
                                type="button"
                                className="admin-panel__primary"
                                onClick={saveDevice}
                                disabled={savingDevice}
                            >
                                {savingDevice ? '儲存中...' : '儲存'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {userModal && userForm && (
                <div className="admin-panel__modal">
                    <div
                        className="admin-panel__modal-backdrop"
                        onClick={closeUserEditor}
                    />
                    <div className="admin-panel__modal-card">
                        <div className="admin-panel__modal-header">
                            <h3>
                                {userModal.mode === 'create' ? '新增用戶' : '編輯用戶'}
                            </h3>
                            <button type="button" onClick={closeUserEditor}>
                                關閉
                            </button>
                        </div>
                        <div className="admin-panel__modal-body admin-panel__modal-body--user">
                            <label>
                                姓名
                                <input
                                    value={userForm.full_name}
                                    onChange={(event) =>
                                        setUserForm((prev) => ({
                                            ...prev,
                                            full_name: event.target.value,
                                        }))
                                    }
                                />
                            </label>
                            <label>
                                Email
                                <input
                                    type="email"
                                    value={userForm.email}
                                    onChange={(event) =>
                                        setUserForm((prev) => ({
                                            ...prev,
                                            email: event.target.value,
                                        }))
                                    }
                                />
                            </label>
                            <label>
                                角色
                                <select
                                    value={userForm.role}
                                    onChange={(event) =>
                                        setUserForm((prev) => ({
                                            ...prev,
                                            role: event.target.value,
                                        }))
                                    }
                                >
                                    {ROLE_OPTIONS.map((option) => (
                                        <option key={option || 'empty'} value={option}>
                                            {option || '請選擇'}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="admin-panel__field">
                                <span className="admin-panel__label-row">
                                    <span>密碼</span>
                                    <span className="admin-panel__label-hint">
                                        {userModal.mode === 'create'
                                            ? '必填'
                                            : '留空則不變更'}
                                    </span>
                                </span>
                                <input
                                    type="password"
                                    value={userForm.password}
                                    onChange={(event) =>
                                        setUserForm((prev) => ({
                                            ...prev,
                                            password: event.target.value,
                                        }))
                                    }
                                />
                            </label>
                            <label className="admin-panel__field">
                                <span className="admin-panel__label-row">
                                    <span>邊坡/區段</span>
                                    <span className="admin-panel__label-hint">
                                        會顯示在用戶頁面
                                    </span>
                                </span>
                                <input
                                    value={userForm.slope}
                                    onChange={(event) =>
                                        setUserForm((prev) => ({
                                            ...prev,
                                            slope: event.target.value,
                                        }))
                                    }
                                />
                            </label>
                            <label className="admin-panel__field">
                                <span className="admin-panel__label-row">
                                    <span>單位</span>
                                    <span className="admin-panel__label-hint">
                                        會顯示在用戶頁面
                                    </span>
                                </span>
                                <input
                                    value={userForm.institution}
                                    onChange={(event) =>
                                        setUserForm((prev) => ({
                                            ...prev,
                                            institution: event.target.value,
                                        }))
                                    }
                                />
                            </label>
                            {userModal.mode === 'edit' && (
                                <div className="admin-panel__telegram-row admin-panel__span-2">
                                    <div className="admin-panel__telegram-card">
                                        <div className="admin-panel__telegram-title">
                                            Telegram 狀態
                                        </div>
                                        <div className="admin-panel__telegram-value">
                                            {userForm.telegram_chat_id
                                                ? '已綁定'
                                                : '未綁定'}
                                        </div>
                                    </div>
                                    <div className="admin-panel__telegram-actions">
                                        {userForm.telegram_chat_id && (
                                            <button
                                                type="button"
                                                className="admin-panel__ghost"
                                                onClick={() => removeTelegram(userModal.user)}
                                                disabled={userActionLoading === userForm.id}
                                            >
                                                解除 Telegram
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                            <label className="admin-panel__span-2">
                                備註
                                <input
                                    value={userForm.remark}
                                    onChange={(event) =>
                                        setUserForm((prev) => ({
                                            ...prev,
                                            remark: event.target.value,
                                        }))
                                    }
                                />
                            </label>
                        </div>
                        <div className="admin-panel__modal-footer">
                            {userModal.mode === 'edit' && (
                                <button
                                    type="button"
                                    className="admin-panel__danger"
                                    onClick={() => removeUser(userModal.user)}
                                    disabled={userActionLoading === userForm.id}
                                >
                                    刪除用戶
                                </button>
                            )}
                            <button
                                type="button"
                                className="admin-panel__ghost"
                                onClick={closeUserEditor}
                                disabled={savingUser}
                            >
                                取消
                            </button>
                            <button
                                type="button"
                                className="admin-panel__primary"
                                onClick={saveUser}
                                disabled={savingUser}
                            >
                                {savingUser ? '儲存中...' : '儲存'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {permissionUser && (
                <div className="admin-panel__modal">
                    <div
                        className="admin-panel__modal-backdrop"
                        onClick={closePermissionEditor}
                    />
                    <div className="admin-panel__modal-card admin-panel__modal-card--wide">
                        <div className="admin-panel__modal-header">
                            <div>
                                <h3>編輯權限</h3>
                                <p>
                                    {pickUserName(permissionUser)} ·{' '}
                                    {pickUserEmail(permissionUser)}
                                </p>
                            </div>
                            <button type="button" onClick={closePermissionEditor}>
                                關閉
                            </button>
                        </div>
                        <div className="admin-panel__modal-body admin-panel__modal-body--permissions">
                            <div className="admin-panel__permission-sidebar">
                                <div className="admin-panel__permission-actions">
                                    <button
                                        type="button"
                                        className="admin-panel__ghost"
                                        onClick={() => setAllPermissions(true)}
                                    >
                                        全選
                                    </button>
                                    <button
                                        type="button"
                                        className="admin-panel__ghost"
                                        onClick={() => setAllPermissions(false)}
                                    >
                                        全不選
                                    </button>
                                </div>
                                <div className="admin-panel__permission-list">
                                    {orderedDistricts.map((district) => {
                                        const list = devicesByDistrict.get(district) || [];
                                        const selectableIds = list
                                            .filter(
                                                (device) =>
                                                    !isExcludedFromBulkPermissionSelect(device)
                                            )
                                            .map((device) => getDeviceId(device))
                                            .filter(Boolean);
                                        const selectedCount = selectableIds.filter((id) =>
                                            permissionIds.has(id)
                                        ).length;
                                        const checked =
                                            selectableIds.length > 0 &&
                                            selectedCount === selectableIds.length;
                                        const indeterminate =
                                            selectedCount > 0 &&
                                            selectedCount < selectableIds.length;
                                        return (
                                            <DistrictToggle
                                                key={district}
                                                label={district}
                                                checked={checked}
                                                indeterminate={indeterminate}
                                                onChange={() => toggleDistrictPermissions(district)}
                                                meta={`${selectedCount}/${selectableIds.length}`}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="admin-panel__permission-main">
                                {loadingPermissions ? (
                                    <div className="admin-panel__loading">
                                        載入權限中...
                                    </div>
                                ) : devices.length === 0 ? (
                                    <div className="admin-panel__loading">
                                        尚無可用儀器資料
                                    </div>
                                ) : (
                                    orderedTypeKeys.map((typeKey) => {
                                        const list = devicesByType.get(typeKey) || [];
                                        if (list.length === 0) return null;
                                        return (
                                            <div
                                                key={typeKey}
                                                className="admin-panel__permission-group"
                                            >
                                                <div className="admin-panel__permission-group-title">
                                                    {DEVICE_TYPE_LABELS[typeKey] || typeKey}
                                                </div>
                                                <div className="admin-panel__permission-grid">
                                                    {list.map((device) => {
                                                        const deviceId = getDeviceId(device);
                                                        if (!deviceId) return null;
                                                        return (
                                                            <label
                                                                key={deviceId}
                                                                className="admin-panel__permission-item"
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={permissionIds.has(
                                                                        deviceId
                                                                    )}
                                                                    onChange={() =>
                                                                        toggleDevicePermission(
                                                                            deviceId
                                                                        )
                                                                    }
                                                                />
                                                                <span>
                                                                    {getDeviceName(device)}
                                                                </span>
                                                                <span className="admin-panel__district-tag">
                                                                    {getDeviceDistrict(device)}
                                                                </span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                                {permissionError && (
                                    <div className="admin-panel__alert">
                                        {permissionError}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="admin-panel__modal-footer">
                            <button
                                type="button"
                                className="admin-panel__ghost"
                                onClick={closePermissionEditor}
                                disabled={savingPermissions}
                            >
                                取消
                            </button>
                            <button
                                type="button"
                                className="admin-panel__primary"
                                onClick={savePermissions}
                                disabled={savingPermissions}
                            >
                                {savingPermissions ? '儲存中...' : '更新權限'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
